import { ElementRef } from '@angular/core';

import { APP_COMPONENT_SELECTOR } from 'src/app/app.component';

/**
 * Checks if an element is a descendant of another element matching the CSS query with OWCC boundaries.
 *
 * @param currentElement - The element to check.
 * @param query - The CSS selector query.
 * @param win - The window object which is crawled.
 * @returns - returns the element matchting the query or undefined
 *
 * @example
 * // Assuming we have an HTML structure like this:
 * // <div id="app-root">
 * //   <div class="container">
 * //     <p>Some content</p>
 * //   </div>
 * // </div>
 *
 * const containerElement = document.querySelector('.container');
 * const isDescendant = isElementWithinQuery(containerElement, '#app-root');
 * // Result: true (since .container is a descendant of #app-root)
 *
 * const nonDescendantElement = document.querySelector('.non-existent');
 * const isNonDescendant = isElementWithinQuery(nonDescendantElement, '#app-root');
 * // Result: false (since .non-existent is not a descendant of #app-root)
 */
export function isElementWithinQuery(
  currentElement: HTMLElement | ElementRef<HTMLElement>,
  query: string,
  win: Window
): HTMLElement | undefined {
  const elem: HTMLElement = currentElement instanceof ElementRef ? currentElement.nativeElement : currentElement;

  if (!elem) {
    throw new Error('Element is not defined');
  }

  if (typeof win?.document?.querySelector !== 'function') {
    throw new TypeError('Cannot find querySelector function');
  }

  const appRootElement: HTMLElement | null = win.document.querySelector(APP_COMPONENT_SELECTOR);

  if (appRootElement === null) {
    throw new Error('Cannot find app root element in DOM');
  }

  const matchedElement: HTMLElement | null = elem.closest(query);

  if (!matchedElement) {
    // element is not within the query
    return undefined;
  }

  const isBeyondOwccBoundries: boolean =
    matchedElement.contains(appRootElement) && !matchedElement?.isEqualNode(appRootElement);

  return isBeyondOwccBoundries ? undefined : matchedElement;
}
